import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection, addDoc, updateDoc, doc, serverTimestamp,
  getDocs, query, where, writeBatch,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useAllTaskCategories } from '../../hooks/useCompanyData';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import Modal from '../../components/common/Modal';
import { Plus, Edit, ToggleLeft, ToggleRight, ChevronUp, ChevronDown, RefreshCw } from 'lucide-react';

const TYPES = ['production', 'support', 'delay', 'other'];

export default function AdminCategoriesPage() {
  const { t } = useTranslation();
  const { categories, loading } = useAllTaskCategories();
  const { user, companyId } = useAuth();
  const toast = useToast();

  const [showForm, setShowForm] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'production' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [reordering, setReordering] = useState(false);

  const [customNames, setCustomNames] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(false);

  useEffect(() => {
    if (companyId) loadCustomNames();
  }, [companyId]);

  const loadCustomNames = async () => {
    if (!companyId) return;
    setLoadingCustom(true);
    try {
      const snap = await getDocs(
        query(
          collection(db, COLLECTIONS.PRODUCTION_TASKS),
          where('companyId', '==', companyId),
          where('isCustomTaskName', '==', true),
        )
      );
      const map = {};
      snap.forEach((d) => {
        const { customTaskName, taskType } = d.data();
        if (customTaskName) {
          if (!map[customTaskName]) map[customTaskName] = { name: customTaskName, type: taskType || 'other', count: 0 };
          map[customTaskName].count++;
        }
      });
      setCustomNames(Object.values(map).sort((a, b) => b.count - a.count));
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCustom(false);
    }
  };

  const openAdd = () => {
    setEditCat(null);
    setForm({ name: '', type: 'production' });
    setErrors({});
    setShowForm(true);
  };

  const openEdit = (cat) => {
    setEditCat(cat);
    setForm({ name: cat.name, type: cat.type });
    setErrors({});
    setShowForm(true);
  };

  const promoteToCategory = (name, type) => {
    setEditCat(null);
    setForm({ name, type });
    setErrors({});
    setShowForm(true);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = t('validation.required');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editCat) {
        await updateDoc(doc(db, COLLECTIONS.TASK_CATEGORIES, editCat.id), {
          name: form.name.trim(),
          type: form.type,
          updatedAt: serverTimestamp(),
        });
      } else {
        const maxOrder = categories.reduce((m, c) => Math.max(m, c.sortOrder || 0), 0);
        await addDoc(collection(db, COLLECTIONS.TASK_CATEGORIES), {
          companyId,
          name: form.name.trim(),
          type: form.type,
          isDefault: false,
          isActive: true,
          sortOrder: maxOrder + 1,
          createdBy: user.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      toast(t(editCat ? 'toast.categoryUpdated' : 'toast.categorySaved'), 'success');
      setShowForm(false);
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (cat) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.TASK_CATEGORIES, cat.id), {
        isActive: !cat.isActive,
        updatedAt: serverTimestamp(),
      });
      toast(t('toast.categoryUpdated'), 'success');
    } catch {
      toast(t('toast.saveFailed'), 'error');
    }
  };

  const handleReorder = async (cat, direction) => {
    if (reordering) return;
    const sorted = [...categories].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const idx = sorted.findIndex((c) => c.id === cat.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const swapCat = sorted[swapIdx];
    setReordering(true);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, COLLECTIONS.TASK_CATEGORIES, cat.id), {
        sortOrder: swapCat.sortOrder,
        updatedAt: serverTimestamp(),
      });
      batch.update(doc(db, COLLECTIONS.TASK_CATEGORIES, swapCat.id), {
        sortOrder: cat.sortOrder,
        updatedAt: serverTimestamp(),
      });
      await batch.commit();
    } catch {
      toast(t('toast.saveFailed'), 'error');
    } finally {
      setReordering(false);
    }
  };

  const sorted = [...categories].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{t('admin.taskCategories')}</h1>
          <p className="page-header-sub">{t('categories.pageSubtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <Plus size={18} />{t('categories.addCategory')}
        </button>
      </div>

      <div className="page-content-wide">
        {/* ── Preset categories ── */}
        <div className="card">
          <div className="card-header">
            <div>
              <span className="card-title">{t('categories.presetCategories')}</span>
              <p className="text-sm text-muted" style={{ marginTop: 2 }}>
                {t('categories.presetCategoriesHint')}
              </p>
            </div>
            <span className="badge badge-other">{sorted.length}</span>
          </div>
          {loading ? (
            <div className="page-loading" style={{ minHeight: 200 }}><div className="spinner" /></div>
          ) : sorted.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--space-8)' }}>
              <p className="text-sm text-muted">{t('categories.noCategories')}</p>
            </div>
          ) : (
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 72 }}>{t('categories.reorder')}</th>
                    <th>{t('categories.categoryName')}</th>
                    <th>{t('categories.categoryType')}</th>
                    <th>{t('categories.isDefault')}</th>
                    <th>{t('categories.isActive')}</th>
                    <th>{t('taskTable.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((cat, idx) => (
                    <tr key={cat.id} style={{ opacity: cat.isActive ? 1 : 0.5 }}>
                      <td>
                        <div className="flex gap-1">
                          <button
                            className="btn btn-icon"
                            onClick={() => handleReorder(cat, 'up')}
                            disabled={idx === 0 || reordering}
                            title={t('categoriesExtra.moveUp')}
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            className="btn btn-icon"
                            onClick={() => handleReorder(cat, 'down')}
                            disabled={idx === sorted.length - 1 || reordering}
                            title={t('categoriesExtra.moveDown')}
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                      </td>
                      <td className="font-semibold">{cat.name}</td>
                      <td><span className={`badge badge-${cat.type}`}>{t(`categories.${cat.type}`)}</span></td>
                      <td>{cat.isDefault ? <span className="badge badge-active">Default</span> : <span className="text-muted text-xs">—</span>}</td>
                      <td>
                        <button className="btn btn-icon" onClick={() => handleToggleActive(cat)} title={cat.isActive ? t('categoriesExtra.deactivate') : t('categoriesExtra.activate')}>
                          {cat.isActive
                            ? <ToggleRight size={20} style={{ color: 'var(--color-brand)' }} />
                            : <ToggleLeft size={20} />}
                        </button>
                      </td>
                      <td>
                        <button className="btn btn-icon btn-sm" onClick={() => openEdit(cat)}>
                          <Edit size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ── Custom task names ── */}
        <div className="card">
          <div className="card-header">
            <div>
              <span className="card-title">{t('categories.customTaskNames')}</span>
              <p className="text-sm text-muted" style={{ marginTop: 2 }}>
                {t('categories.customTaskNamesHint')}
              </p>
            </div>
            <button className="btn btn-sm btn-secondary" onClick={loadCustomNames} disabled={loadingCustom}>
              <RefreshCw size={14} />
            </button>
          </div>

          {loadingCustom ? (
            <div className="page-loading" style={{ minHeight: 80 }}><div className="spinner" /></div>
          ) : customNames.length === 0 ? (
            <div className="empty-state" style={{ padding: 'var(--space-6)' }}>
              <p className="text-sm text-muted">{t('categories.noCustomTasks')}</p>
            </div>
          ) : (
            <div className="table-container" style={{ borderRadius: 0, border: 'none' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('categories.categoryName')}</th>
                    <th>{t('categories.categoryType')}</th>
                    <th>{t('categories.timesUsed')}</th>
                    <th>{t('taskTable.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {customNames.map((entry) => (
                    <tr key={entry.name}>
                      <td className="font-semibold">{entry.name}</td>
                      <td><span className={`badge badge-${entry.type}`}>{t(`categories.${entry.type}`)}</span></td>
                      <td className="text-sm text-muted">{entry.count}×</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => promoteToCategory(entry.name, entry.type)}
                          title={t('categories.promoteToCategory')}
                        >
                          <Plus size={13} />
                          {t('categories.promoteToCategory')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <Modal
          title={editCat ? t('categories.editCategory') : t('categories.addCategory')}
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={saving}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="spinner" /> : t('common.save')}
              </button>
            </>
          }
        >
          <div className="form-group">
            <label className="form-label">{t('categories.categoryName')} <span className="required">*</span></label>
            <input
              className={`form-control ${errors.name ? 'is-error' : ''}`}
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              autoFocus
            />
            {errors.name && <p className="form-error">{errors.name}</p>}
          </div>
          <div className="form-group">
            <label className="form-label">{t('categories.categoryType')}</label>
            <select
              className="form-control"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>{t(`categories.${type}`)}</option>
              ))}
            </select>
          </div>
        </Modal>
      )}
    </div>
  );
}
